import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import Modal from "src/components/modals/Modal"

import "./ArtShowcaseModal.css"

/* One picture, as big as the screen allows, and an entrance worth pressing a portrait for.

   The unit plates are 1189×1323 and the game shows them at 162px in the unit sheet, 48px in the
   march picker and 44px on a card — a poster viewed through a keyhole. Pressing any of those
   opens this (owner's ask, 2026-09-06): the plate fills the sheet, and it arrives the way the
   landing screen does — in the terminal voice, a target being acquired. A push in from a tight
   crop, a scan sweeping down the plate, four corner brackets locking on, the name landing with
   one chromatic split, and then a slow drift so the picture never quite holds still. Every piece
   of that is CSS in ArtShowcaseModal.css, and every piece of it stops under "reduce motion",
   which leaves the plate, the name and the brackets exactly where they end up anyway.

   Pressing the plate again zooms in on the point pressed (`transform-origin` at the pointer),
   which is where the pixel art starts to read as pixels; pressing it once more steps back.

   It knows nothing about what it shows. The caller hands it a `src`, a `title`, a `kicker`
   ("Unit · Humans"), an optional `subtitle` and `lines`, and a `team` that only chooses the colour
   of the light. That is what lets a unit, a building and a shop class share one screen — and what
   keeps it out of the rules: nothing here reads game state, and nothing the game does reads this. */

const CLOSE_UP_SCALE = 2.6

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches

const ArtShowcaseModal = ({ src, title, kicker, subtitle, lines = [], team = "human" }) => {
  const { t } = useTranslation()
  const stage = useRef(null)
  // Where the player pressed, as a percentage of the picture, or null when showing the whole plate.
  const [closeUp, setCloseUp] = useState(null)

  const toggleCloseUp = (e) => {
    if (closeUp) {
      setCloseUp(null)
      return
    }
    // A keyboard press has no position (`detail` is 0 for Enter and Space); zoom on the centre.
    const box = e.currentTarget.getBoundingClientRect()
    const fromPointer = e.detail > 0 && box.width > 0 && box.height > 0
    setCloseUp(
      fromPointer
        ? { x: ((e.clientX - box.left) / box.width) * 100, y: ((e.clientY - box.top) / box.height) * 100 }
        : { x: 50, y: 50 }
    )
  }

  /* A little parallax under the pointer: the plate leans away and the brackets lean with it, which
     is what makes the brackets read as glass over a picture rather than lines on it. Written as
     CSS variables straight onto the node — a state update per mouse move would re-render the
     sheet a hundred times a second for a 8px shift. */
  const lean = (e) => {
    const node = stage.current
    if (!node || prefersReducedMotion()) return
    const box = node.getBoundingClientRect()
    if (!box.width || !box.height) return
    node.style.setProperty("--px", String(((e.clientX - box.left) / box.width) * 2 - 1))
    node.style.setProperty("--py", String(((e.clientY - box.top) / box.height) * 2 - 1))
  }
  const settle = () => {
    const node = stage.current
    if (!node) return
    node.style.setProperty("--px", "0")
    node.style.setProperty("--py", "0")
  }

  const hint = closeUp ? t("showcase.hintBack") : t("showcase.hintCloser")

  return (
    <Modal width="min(96vw, 92vh)" maxHeight="94vh" top="3vh">
      <div className={`showcase ${team}`} onMouseMove={lean} onMouseLeave={settle}>
        <div className="showcase-stage" ref={stage}>
          <div className="showcase-plate">
            <button
              type="button"
              className="showcase-picture"
              onClick={toggleCloseUp}
              aria-pressed={closeUp !== null}
              aria-label={hint}
              title={hint}
            >
              <img
                src={src}
                alt=""
                className={closeUp ? "close-up" : undefined}
                style={
                  closeUp
                    ? { transformOrigin: `${closeUp.x}% ${closeUp.y}%`, transform: `scale(${CLOSE_UP_SCALE})` }
                    : undefined
                }
              />
            </button>
            <span className="showcase-sweep" aria-hidden="true" />
            <span className="showcase-scanlines" aria-hidden="true" />
          </div>
          <div className="showcase-corners" aria-hidden="true">
            <span className="showcase-corner tl" />
            <span className="showcase-corner tr" />
            <span className="showcase-corner bl" />
            <span className="showcase-corner br" />
          </div>
        </div>

        <div className="showcase-caption">
          {kicker ? <p className="showcase-kicker">{kicker}</p> : null}
          <h2 className="showcase-title">{title}</h2>
          {subtitle ? <p className="showcase-subtitle">{subtitle}</p> : null}
          {lines.filter(Boolean).map((line, index) => (
            <p className="showcase-line" key={index}>
              {line}
            </p>
          ))}
          <p className="showcase-hint">{hint}</p>
        </div>
      </div>
    </Modal>
  )
}

export default ArtShowcaseModal
